import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import AuthLoginLoginForm from 'tio-employee/components/auth/login/login-form';
import Component from '@glimmer/component';
import EmailLookupWrapper from 'tio-employee/components/auth/email-lookup-wrapper';
import emailSchema from 'tio-common/validation-schema/models/email';
import IntlService from 'ember-intl/services/intl';
import NotLaunched from 'tio-employee/components/auth/register/not-launched';
import pageTitle from 'ember-page-title/helpers/page-title';
import type LoginController from 'tio-employee/controllers/login';
import type Owner from '@ember/owner';
import type PreRegisterService from '../services/pre-register';
import { safeParse } from 'valibot';

export interface LoginRouteComponentSignature {
  Args: {
    controller: LoginController;
  };
}

export default class LoginRoute extends Component<LoginRouteComponentSignature> {
  @service declare preRegister: PreRegisterService;
  @service declare intl: IntlService;

  @tracked email = '';
  @tracked emailLookupError: string | null = null;
  @tracked emailLookupIsLoading = false;

  constructor(owner: Owner, args: LoginRouteComponentSignature['Args']) {
    super(owner, args);
    this.email = decodeURIComponent(this.args.controller.email || '');
  }

  get forceEmailLogin() {
    return this.args.controller.emailLogin;
  }

  get validationErrors() {
    const result = safeParse(emailSchema, { email: this.email });
    return result.success ? [] : result.issues.map((issue) => issue.message);
  }

  get settings() {
    return this.preRegister.settings;
  }

  get company() {
    return this.preRegister.sparseCompany;
  }

  get companyName() {
    return this.company?.name || '';
  }

  get isUsingSso() {
    return !!(
      !this.forceEmailLogin &&
      this.settings?.ssoAuthUrl &&
      this.settings?.idProvider &&
      this.settings?.idProvider !== 'NONE'
    );
  }

  get selfRegisterCompany() {
    return this.preRegister.settings.register['isSelfRegisterAllowed'] || false;
  }

  get signUpRoute() {
    return this.selfRegisterCompany ? '/self-registration' : '/register';
  }

  get showNotLaunched() {
    return !this.preRegister.isLaunched && !!this.preRegister.launchDate;
  }

  <template>
    {{pageTitle (t "login.page_title")}}
    <EmailLookupWrapper @route="login">
      {{#if this.showNotLaunched}}
        <NotLaunched
          @companyName={{this.companyName}}
          @launchDate={{this.preRegister.launchDate}}
          @daysUntilLaunch={{this.preRegister.daysUntilLaunch}}
        />
      {{else if this.isUsingSso}}
        <div class="text-center mb-6">
          <h1 class="text-2xl font-semibold my-2">
            {{t "login.sso_redirect"}}
          </h1>
        </div>
      {{else}}
        <div class="text-center mb-6">
          <h1 class="text-2xl font-semibold my-2">
            {{t "login.log_in"}}
          </h1>

          <div class="mb-6 text-md">
            {{t "login.new_to_tuition"}}
            <a class="underline text-ocean-600" href={{this.signUpRoute}}>
              {{t "login.sign_up"}}
            </a>
          </div>
        </div>
        <p class="text-base text-gray-600 text-center mb-4">
          {{t "login.enter_email_and_pw"}}
        </p>
        <AuthLoginLoginForm @email={{this.email}} />
      {{/if}}
    </EmailLookupWrapper>
  </template>
}
